import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { useUserPreferences } from "@/stores/useUserPreferences";

/**
 * Số dòng / trang theo người dùng: chọn ở Cài đặt người dùng hoặc ở ô số dòng dưới bất kỳ danh sách nào thì
 * mọi danh sách dùng số đó, kể cả khi mở chi tiết rồi quay lại hay tải lại trang (trước đây quay về mặc định 20 / 25).
 * Người dùng chưa chọn lần nào thì mỗi danh sách giữ mặc định riêng của nó.
 */
export function usePreferredPageSize(fallback: number): number {
  const size = useUserPreferences((s) => s.tablePageSize);
  // Bản cũ chỉ lưu tablePageSize (mặc định 25) — số khác 25 nghĩa là người dùng đã chọn ở Cài đặt
  const isSet = useUserPreferences((s) => s.tablePageSizeSet || s.tablePageSize !== 25);
  return isSet && size > 0 ? size : fallback;
}

/** Ghi lại số dòng / trang người dùng vừa chọn (gọi từ ô chọn số dòng của ERPPagination và Cài đặt người dùng) */
export function rememberPageSize(size: number) {
  if (!Number.isFinite(size) || size <= 0) return;
  useUserPreferences.setState({ tablePageSize: size, tablePageSizeSet: true });
}

/** Thay cho useState(số) ở danh sách tự giữ số dòng / trang: khởi tạo theo lựa chọn của người dùng */
export function usePageSizeState(fallback: number): [number, Dispatch<SetStateAction<number>>] {
  const preferred = usePreferredPageSize(fallback);
  const [size, setSize] = useState<number>(preferred);
  const touched = useRef(false);
  // Cài đặt người dùng nạp sau lần vẽ đầu (tải lại trang): theo số đã chọn khi người dùng chưa đổi ở trang này
  useEffect(() => {
    if (!touched.current) setSize(preferred);
  }, [preferred]);
  const set = useCallback<Dispatch<SetStateAction<number>>>((next) => {
    touched.current = true;
    setSize(next);
  }, []);
  return [size, set];
}
